<template>
  <b-card-text class="container-mobile">
    <loading :active.sync="isLoading" is-full-page loader="bars" color="#00A09C" :width="64" />
    <b-row class="data-card-details">
      <b-col sm="12" md="6" class="pb-3 px-4">
        <b-input-group v-if="!forMultipleInvoice">
          <b-form-input v-model="filter" type="text" :placeholder="FormMSG(31, 'Type to search ...')" @keyup.enter="filterInvoices" @change="filterInvoices" />
          <b-input-group-append>
            <b-input-group-text>
              <component :is="getLucideIcon(ICONS.SEARCH.name)" v-if="filter.length === 0" color="#06263E" :size="16" :stroke-width="2.5" />
              <component :is="getLucideIcon(ICONS.X.name)" v-else color="#06263E" :size="16" :stroke-width="2.5" @click="reinitializeFilterInvoices" />
            </b-input-group-text>
          </b-input-group-append>
        </b-input-group>
      </b-col>
      <b-col
        v-if="forApprove || forMultipleInvoice"
        sm="12"
        md="6"
        class="pb-3 px-4 d-flex align-items-center"
        :class="maxMobileScreenWidth ? 'justify-content-center' : 'justify-content-end'"
      >
        <div v-if="forApprove && canMarkEntryAsProcessed" class="d-flex align-items-center" style="gap: 5px">
          <b-button
            v-b-tooltip="FormMSG(45, 'View background tasks')"
            :size="maxMobileScreenWidth ? 'sm' : 'md'"
            variant="light"
            class="px-4 text-dark"
            style="gap: 7px"
            @click="loadJobsBackgroundList"
          >
            <component :is="getLucideIcon('ClipboardList')" :size="maxMobileScreenWidth ? 16 : 20" />
          </b-button>
          <b-button
            :size="maxMobileScreenWidth ? 'sm' : 'md'"
            variant="dark"
            class="px-4"
            :class="{ 'bg-gray-800': isPendingOdooRequest, 'bg-gray-700': !isPendingOdooRequest, 'mr-3': !maxMobileScreenWidth }"
            :disabled="isPendingOdooRequest"
            style="gap: 7px"
            @click="loadOdooElectronicInvoice"
          >
            <div v-if="isPendingOdooRequest" class="d-flex justify-content-center align-items-center">
              <b-spinner small type="grow" class="mr-2"></b-spinner>
              <span :class="{ small: maxMobileScreenWidth }">{{ FormMSG(46, 'Import in progress...') }}</span>
            </div>
            <div v-else class="d-flex justify-content-center align-items-center">
              <component :is="getLucideIcon('DownloadCloud')" class="mr-2" :size="maxMobileScreenWidth ? 16 : 20" />
              <span :class="{ small: maxMobileScreenWidth }">{{ FormMSG(44, 'Load Odoo Electronic Invoice') }}</span>
            </div>
          </b-button>
        </div>
        <b-button
          v-if="!maxMobileScreenWidth && !maxTabletScreenWidth"
          size="md"
          variant="blue-sapphire"
          class="w-200-px d-flex justify-content-center align-items-center"
          style="gap: 7px"
          :disabled="invoicesItems.length === 0"
          @click="openReviewMode"
        >
          <b-spinner v-show="loadingReviewMode" class="mr-2" small />
          <component :is="getLucideIcon('View')" v-if="!loadingReviewMode" :size="20" />
          <span>{{ FormMSG(164, 'Review mode') }}</span>
        </b-button>
      </b-col>
    </b-row>
    <div v-if="$screen.width <= 992" class="container-mobile">
      <div class="my-expense-mobile">
        <b-overlay :show.sync="isBusy" opacity="0.75" rounded="lg">
          <InvoiceItemMobile
            :for-approve="forApprove"
            :for-draft="forDraft"
            :for-pending="forPending"
            :for-approved="forApproved"
            :for-multiple-invoice="forMultipleInvoice"
            :invoice-items="forApprove ? invoicesApproveItems : invoicesItems"
            :not-submitted-state="notSubmittedState"
            :submitted-state="submittedState"
            @approveInvoice="approveInvoice"
            @rejectInvoice="rejectInvoice"
            @sendInvoiceForApproval="sendInvoiceForApproval"
            @deleteInvoice="deleteInvoice"
            @setSelectedItem="setSelectedItem"
            @viewInvoiceDetail="viewInvoiceDetails"
          />
        </b-overlay>
      </div>
    </div>
    <section v-else>
      <div v-if="forApprove" class="container-layout details-document-package p-0">
        <b-overlay :show.sync="isBusy" opacity="0.85" rounded="lg">
          <div v-if="invoicesApproveItems && invoicesApproveItems.length === 0" class="empty-state">
            {{ FormMSG(99, 'There are no invoices to validate yet') }}
          </div>
          <div v-else>
            <b-card v-for="(approveItem, itemIndex) in invoicesApproveItems" :key="approveItem.department" no-body class="card-border-blue-light">
              <b-row
                v-b-toggle="`${approveItem.department}`"
                class="back-with-title cursor-pointer d-flex align-items-center pb-2 pl-0"
                :class="{ 'mt-16': $isPwa() }"
              >
                <b-col cols="6">
                  <h2 class="d-flex align-items-center">
                    <div>{{ approveItem.departmentName }}</div>
                    <b-badge pill class="ml-2">{{ approveItem.invoicesCount }}</b-badge>
                  </h2>
                </b-col>
                <b-col cols="6">
                  <div class="card-header-actions">
                    <b-link class="card-header-action btn-minimize text-white">
                      <component :is="getLucideIcon('ChevronDown')" color="#06263E" :size="20" />
                    </b-link>
                  </div>
                </b-col>
              </b-row>
              <b-collapse :id="`${approveItem.department}`" :visible="itemIndex === 0">
                <b-card-body>
                  <div v-for="functionObj in approveItem.expensesByFunction" :key="functionObj.function">
                    <section v-for="userObj in functionObj.userExpenses" :key="userObj.userId">
                      <h5 class="d-flex align-items-center">
                        {{ userObj.fullName }} - {{ functionObj.functionName.toUpperCase() }}
                        <b-badge pill class="ml-2">{{ userObj.expenses.length }}</b-badge>
                      </h5>
                      <invoices-datatable
                        for-approve
                        :is-busy="isBusy"
                        :invoices-items="provideCleanInvoice(userObj.expenses)"
                        :invoices-fields="invoicesFields"
                        @row-clicked="
                          (item) =>
                            viewInvoiceDetails(
                              item,
                              invoicesItems.findIndex((x) => +x.id === +item.id)
                            )
                        "
                      />
                    </section>
                  </div>
                </b-card-body>
              </b-collapse>
            </b-card>
          </div>
        </b-overlay>
      </div>
      <invoices-datatable
        v-else
        :for-draft="forDraft"
        :for-pending="forPending"
        :for-approved="forApproved"
        :for-multiple-invoice="forMultipleInvoice"
        :for-efc-draft="forEfcDraft"
        :for-efc-submitted="forEfcSubmitted"
        :is-selected-all="isSelectedAll"
        :is-busy="isBusy"
        :can-see-crew-member="canSeeCrewMember"
        :invoices-items="invoicesItems"
        :invoices-fields="invoicesFields"
        @row-clicked="viewInvoiceDetails"
        @select-all="setSelectionForAll"
        @select="updateSelectedRows"
      />
    </section>
    <b-row v-if="$screen.width >= 576" class="mt-3">
      <!-- This button is no longer used since we remove selection from tab Approve due to the ticket: https://app.clickup.com/t/86c3ftu93 -->
      <!-- <b-col v-if="forApprove" cols="12">
				<b-button :disabled="selectedRows.length === 0" variant="primary" @click="rejectInvoice(selectedRows)">
					{{ FormMSG(267, 'Reject') }} {{ selectedRows.length >= 1 ? `(${selectedRows.length})` : '' }}
				</b-button>
			</b-col> -->
      <b-col v-if="forEfcDraft || forDraft || (forPending && selectedRows.length > 0) || (forMultipleInvoice && selectedRows.length > 0)" cols="12">
        <b-button :disabled="selectedRows.length === 0" variant="secondary" @click="deleteInvoice(selectedRows)">
          {{ FormMSG(268, 'Delete') }} {{ selectedRows.length >= 1 ? `(${selectedRows.length})` : '' }}
        </b-button>
        <!-- <component id="monitors" v-if="forMultipleInvoice" class="cursor-pointer ml-2" :is="getLucideIcon('Video')" :stroke-width="1.5" />
				<b-popover target="monitors" triggers="hover focus">
					<div class="d-flex flex-column">
						<div>Single monitor</div>
						<div class="bg-primary">Multiple monitors</div>
					</div>
				</b-popover> -->
      </b-col>
    </b-row>
    <b-modal
      v-model="modalRejectedInvoice"
      header-class="header-class-modal-doc-package"
      :title="selectedRows.length > 1 ? FormMSG(28, 'Reject these invoices') : FormMSG(26, 'Reject this invoice')"
      class="modal-danger"
      no-close-on-backdrop
      no-close-on-esc
      hide-header-close
    >
      <b-form-group :label="FormMSG(25, 'Enter your comment:')" label-for="comment">
        <b-form-textarea id="comment" ref="rejectComment" v-model="rejectComment" rows="3" />
      </b-form-group>
      <template #modal-footer>
        <div class="w-100 d-flex flex-row justify-content-end align-items-center">
          <b-button size="sm" variant="custom-outline-gray" class="w-138-px" block @click="modalRejectedInvoice = false">
            {{ FormMSG(101, 'Close') }}
          </b-button>
          <b-button size="sm" variant="custom-primary-blue" class="w-138-px ml-2" style="margin-top: -1px" block @click="finaliseInvoiceReject">
            <div :disabled="rejectComment.length === 0" class="d-flex justify-content-center align-items-center">
              <div class="pl-2" style="margin-top: 1px">{{ FormMSG(102, 'Save') }}</div>
            </div>
          </b-button>
        </div>
      </template>
    </b-modal>
    <jobs-progression-modal v-model="openJobsModal" :data="jobsData" :is-busy="isJobsBusy" @close="openJobsModal = false" @refresh="loadJobsBackgroundList" />
  </b-card-text>
</template>

<script>
// utils
import _ from 'lodash';
import { rendCurrency, formatTableDate } from '~helpers';
import { mapGetters, mapActions } from 'vuex';
import { store } from '@/store';

// mixin
import globalMixin from '@/mixins/global.mixin';
import languageMessages from '@/mixins/languageMessages';
import invoicesToolsMixin from '@/mixins/invoicesTools.mixin';

// props
import mapProps from '@/shared/vuePropsMapper';

// cruds
import {
  getInvoicesToValidate,
  getExpenseToValidate,
  getMyExpenses,
  rejectExpenses,
  deleteExpenses,
  getInvoiceEntryForCrew,
  migrateOdooInvoiceToTgs,
  getSpecificExpense
} from '@/cruds/expense.crud';

// constants
import { FINANCES, EXPENSE_TYPE, VALIDATION_TYPE, INVOICE_MENUS, BACKGROUND_TASK_STATUS, INVOICE_SUBTYPE } from '@/shared/constants';
import { Edit, Eye, Send, Trash2 as Trash } from 'lucide-vue';

// components
import InvoiceItemMobile from './InvoiceItemMobile.vue';
import Loading from 'vue-loading-overlay';
import InvoicesDatatable from './InvoicesDatatable.vue';
import JobsProgressionModal from '../../../components/Modals/JobsProgressionModal.vue';
import { getJobBackgroundList } from '../../../cruds/jobs.crud';

export default {
  name: 'InvoicesDataView',
  components: {
    Edit,
    Eye,
    Send,
    Trash,
    InvoicesDatatable,
    InvoiceItemMobile,
    JobsProgressionModal,
    Loading
  },
  mixins: [languageMessages, globalMixin, invoicesToolsMixin],
  props: {
    ...mapProps(['forApprove', 'forDraft', 'forPending', 'forApproved', 'forMultipleInvoice', 'forEfcDraft', 'forEfcSubmitted'], {
      type: Boolean,
      default: false
    }),
    loadingReviewMode: {
      type: Boolean,
      default: false
    },
    canApprove: {
      type: Boolean,
      default: true
    },
    notSubmittedState: {
      type: Boolean,
      default: false
    },
    submittedState: {
      type: Boolean,
      default: false
    },
    canSeeCrewMember: {
      type: Boolean,
      default: false
    },
    invoiceList: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      filter: '',
      currentPage: 1,
      hv: 'dark',
      invoiceType: 1,
      invoicesApproveItems: [],
      invoicesItems: [],
      selectedRows: [],
      isSelectedAll: false,
      offset: null,
      limit: null,
      rejectComment: '',
      selectedInvoice: null,
      modalRejectedInvoice: false,
      isBusy: false,
      isLoading: false,
      sortBy: 'date',
      sortDesc: true,
      openJobsModal: false,
      isJobsBusy: false,
      jobsData: []
    };
  },
  computed: {
    ...mapGetters({
      isPendingOdooRequest: 'odoo-invoices/isPendingOdooRequest'
    }),
    invoicesFields() {
      const fields = [
        {
          key: 'invoiceNumber',
          label: this.FormMSG(2, 'Inv. no.'),
          formatter: (v) => `${v}`,
          sortable: true,
          class: 'text-center vertical-centered'
        },
        {
          key: 'date',
          label: this.FormMSG(3, 'Date'),
          formatter: (v) => formatTableDate(v),
          sortable: true,
          class: 'text-center vertical-centered'
        },
        {
          key: 'createdAt',
          label: this.FormMSG(33, 'Due date'),
          formatter: (v) => formatTableDate(v),
          sortable: true,
          class: 'text-center vertical-centered'
        },
        {
          key: 'description',
          label: this.FormMSG(6, 'Description'),
          sortable: true,
          class: 'text-center vertical-centered',
          thStyle: { color: '#fff', width: '250px', minWidth: '100px', maxWidth: '300px' },
          tdStyle: { minWidth: '250px', maxWidth: '300px' }
        },
        {
          key: 'po',
          label: this.FormMSG(7, 'PO'),
          formatter: (v) => v,
          sortable: true,
          class: 'text-center vertical-centered',
          thStyle: { 'min-width': '100px' }
        }
      ];
      const supplierField = {
        key: 'supplierName',
        label: this.FormMSG(5, 'Supplier'),
        sortable: true,
        class: 'text-center vertical-centered',
        thStyle: { 'min-width': '150px' }
      };

      const crewField = {
        key: 'crew_member',
        label: this.FormMSG(21, 'Crew Member'),
        sortable: true,
        class: 'text-center vertical-centered',
        thStyle: { 'min-width': '150px' }
      };

      if (this.canSeeCrewMember) {
        fields.splice(1, 0, crewField);
      }

      if (!this.forMultipleInvoice) {
        if (!this.forEfcDraft && !this.forEfcSubmitted) fields.splice(3, 0, supplierField);

        fields.splice(1, 0, {
          key: 'validatedStatus',
          label: this.FormMSG(8, 'Approvals'),
          sortable: true,
          class: 'text-center vertical-centered',
          thStyle: { 'min-width': '100px' }
        });
      }
      fields.push({
        key: 'amountTotal',
        label: this.FormMSG(9, 'Amount'),
        formatter: (v) => rendCurrency(v),
        sortable: true,
        sortByFormatted: true,
        thClass: 'text-center',
        class: 'text-right vertical-centered'
      });

      const selectField = {
        key: 'select',
        class: 'text-center vertical-centered',
        thStyle: { width: '70px' }
      };

      const idField = {
        key: 'id',
        label: '#ID',
        class: 'text-center vertical-centered',
        thStyle: { width: '100px' }
      };

      const encoderField = {
        key: 'encoder',
        label: this.FormMSG(40, 'Encoded by'),
        formatter: (v) => `${v.name} ${v.firstName}`,
        class: 'text-center vertical-centered'
      };

      if (!this.forApprove && !this.forApproved && !this.forEfcSubmitted) fields.unshift(selectField);
      if (this.forApprove) {
        fields.unshift(idField);
        fields.splice(3, 0, encoderField);
      }

      return fields;
    },
    currentRouteQueryTabs() {
      return this.$route.query.tabs;
    },
    getInvoiceStatusMenu() {
      return this.FormMenu(INVOICE_MENUS.STATUS);
    },
    canMarkEntryAsProcessed() {
      return store.canMarkEntryAsProcessed();
    }
  },
  watch: {
    canApprove: {
      async handler(newVal) {
        await this.initData();
      }
    }
  },
  async mounted() {
    await this.$nextTick();
    await this.initData();
    if (!this.forMultipleInvoice && !this.forEfcDraft && !this.forEfcSubmitted && this.isPendingOdooRequest) {
      this.loadJobsBackgroundList();
    }
    this.$bus.$on('invoice:reload-data', async () => {
      await this.initData();
    });
  },
  destroyed() {
    // shutdown event bus
    this.$bus.$off('invoice:reload-data');
  },
  methods: {
    ...mapActions({
      setNewOdooPendingStatus: 'odoo-invoices/setNewOdooPendingStatus'
    }),
    async loadOdooElectronicInvoice() {
      this.setNewOdooPendingStatus(true);
      try {
        await migrateOdooInvoiceToTgs();
        await this.loadJobsBackgroundList();
      } catch (error) {
        console.log('Error to  loadOdooElectronicInvoice >>>>>>>>>', error);
        const formattedError = this.resolveGqlError(error);
        this.createToastForMobile(this.FormMSG(47, 'Error'), formattedError, '', 'danger');
        this.setNewOdooPendingStatus(false);
      }
    },
    async loadJobsBackgroundList() {
      this.isJobsBusy = true;
      this.openJobsModal = true;
      try {
        this.jobsData = await getJobBackgroundList();
        const isSomeJobsPending = this.jobsData.some((job) => job.status === BACKGROUND_TASK_STATUS.IN_PROGRESS);
        this.setNewOdooPendingStatus(isSomeJobsPending);
        if (!isSomeJobsPending) {
          this.createToastForMobile(this.FormMSG(30, 'Success'), this.FormMSG(48, 'All your background tasks are completed'));
          await this.initData();
        }
      } catch (error) {
        console.log('Error to  loadJobsBackgroundList >>>>>>>>>', error);
        const formattedError = this.resolveGqlError(error);
        this.createToastForMobile(this.FormMSG(47, 'Error'), formattedError, '', 'danger');
      }
      this.isJobsBusy = false;
    },
    isInvoiceRefused(item) {
      return [VALIDATION_TYPE.REFUSED_LEVEL_1, VALIDATION_TYPE.REFUSED_LEVEL_2].includes(item.validated);
    },
    async reinitializeFilterInvoices() {
      this.filter = '';
      await this.filterInvoices();
    },
    approveInvoice(invoice) {
      this.$emit('approveInvoice', invoice);
    },
    sendInvoiceForApproval(invoice) {
      this.$emit('sendInvoiceForApproval', invoice);
    },
    rejectInvoice(invoice) {
      this.rejectComment = '';
      this.selectedInvoice = invoice;
      this.modalRejectedInvoice = true;
    },
    deleteInvoice(invoices) {
      const action = () => {
        let invoiceIds = invoices.map((x) => +x.id);
        deleteExpenses(invoiceIds).then(async () => {
          if (this.forMultipleInvoice) {
            this.$emit('row:delete', invoiceIds);
            this.invoicesItems = this.invoicesItems.filter((invoice) => !invoiceIds.includes(+invoice.id));
          } else {
            await this.initData();
          }
          this.createToastForMobile(this.FormMSG(30, 'Success'), this.FormMSG(29, 'Deleting invoices, Done'));
        });
      };
      this.confirmModal(action, this.FormMSG(269, 'Are you sure to delete these invoices?'), '', 'danger');
    },
    async finaliseInvoiceReject() {
      const ids = this.selectedRows.map((item) => parseInt(item.id));
      const comment = this.rejectComment;
      try {
        const isRejected = await rejectExpenses(ids, comment);
        if (isRejected) this.initData();
      } catch (error) {
        console.log('Error on call service rejectExpenses >>>', error);
      }
      this.modalRejectedInvoice = false;
    },
    async filterInvoices() {
      const actionForLoader = async () => {
        await this.initData();
      };
      this.$nextTick(async () => {
        if (this.forApprove) {
          await this.initData();
        } else {
          await this.showLoader(actionForLoader, null);
        }
      });
    },
    handleSortFormatter(invoices) {
      const sortField = this.invoicesFields.find((field) => field.key === this.sortBy);
      if (sortField) {
        const sortOrder = this.sortDesc ? 'desc' : 'asc';
        const formatter = sortField.formatter ? sortField.formatter : () => {};
        const sortByFormatted = sortField.sortByFormatted ? sortField.sortByFormatted : false;
        const sortedVal = this.sortArrayByAttribute(_.cloneDeep(invoices), this.sortBy, sortOrder, formatter, sortByFormatted);

        return sortedVal;
      }
      return invoices;
    },
    async initData() {
      try {
        // reinitialize selections
        this.isBusy = true;
        this.selectedRows = [];
        this.emitSelection();

        let newFilterActive = {
          arrayType: [{ type: EXPENSE_TYPE.INVOICE }],
          recordOffSet: this.offset,
          recordLimit: this.limit,
          keyWord: this.filter
        };

        // initialize data
        if (this.invoiceList.length > 0 && this.forMultipleInvoice) {
          this.invoicesItems = _.cloneDeep(this.handleSortFormatter(this.invoiceList));
        } else {
          if (this.forApprove) {
            newFilterActive = {
              ...newFilterActive,
              arraySubType: [{ type: INVOICE_SUBTYPE.NORMAL_INVOICE }, { type: INVOICE_SUBTYPE.ELEC_INVOICE }]
            };
            const approveItems = await getInvoicesToValidate(newFilterActive);
            if (approveItems) {
              const sortedApproveItems = approveItems.sort((a, b) => a.departmentName.localeCompare(b.departmentName)); // to order result by department alphabetically
              this.invoicesApproveItems = sortedApproveItems.map((item) => {
                return {
                  ...item,
                  invoicesCount: item.expensesByFunction.reduce((acc, cur) => acc + cur.userExpenses.flatMap((user) => user.expenses).length, 0)
                };
              });
              const records = sortedApproveItems.flatMap((item) =>
                item.expensesByFunction.flatMap((func) => func.userExpenses.flatMap((user) => user.expenses))
              );
              this.invoicesItems = this.provideCleanInvoice(records);
            }
          } else if (this.forDraft) {
            newFilterActive = {
              ...newFilterActive,
              arrayStatus: [
                { identifier: VALIDATION_TYPE.NOT_SUBMITTED },
                { identifier: VALIDATION_TYPE.REFUSED_LEVEL_1 },
                { identifier: VALIDATION_TYPE.REFUSED_LEVEL_2 }
              ]
            };
            const records = await getMyExpenses(newFilterActive);
            this.invoicesItems = this.provideCleanInvoice(records);
          } else if (this.forPending) {
            newFilterActive = {
              ...newFilterActive,
              arrayStatus: [{ identifier: VALIDATION_TYPE.SUBMITTED }, { identifier: VALIDATION_TYPE.VALIDATED_LEVEL_1 }]
            };
            const records = await getMyExpenses(newFilterActive);
            this.invoicesItems = this.provideCleanInvoice(records);
          } else if (this.forApproved || this.forMultipleInvoice) {
            newFilterActive = {
              ...newFilterActive,
              arrayStatus: [{ identifier: VALIDATION_TYPE.VALIDATED_LEVEL_2 }]
            };
            const records = await getMyExpenses(newFilterActive);
            this.invoicesItems = this.provideCleanInvoice(records);
          } else if (this.forEfcDraft) {
            const records = await getInvoiceEntryForCrew(newFilterActive);
            this.invoicesItems = this.provideCleanInvoice(records);
          } else if (this.forEfcSubmitted) {
            const records = await getInvoiceEntryForCrew(newFilterActive, true);
            this.invoicesItems = this.provideCleanInvoice(records);
          }
        }

        this.isBusy = false;
      } catch (error) {
        console.log(error);
        this.isBusy = false;
      }
    },
    provideCleanInvoice(records) {
      // methods called inside map is implemented inside the InvoicesTools.mixin
      const newRecords = records.map((invoice) => {
        return {
          ...invoice,
          validatedStatus: this.getValidatedText(invoice.validated),
          typeString: this.getTypeText(invoice.type),
          _rowVariant: '',
          validatedClass: this.getValidatedColor(invoice.validated),
          statusClass: this.getStatusClass(invoice.validated),
          selectState: false,
          userCanValidate: true // only used as flag when merging electronic invoice to normal invoice.
        };
      });

      return this.handleSortFormatter(newRecords);
    },
    openReviewMode() {
      if (this.forApprove) this.$emit('list:electronic');
      this.$emit('open:review-mode', this.handleSortFormatter(this.invoicesItems));
    },
    async viewInvoiceDetails(invoice, index, latestSelectedElectronicInvoiceId = null) {
      this.isLoading = true;
      // sort the list accordign to the table sort actions.
      const formattedInvoicesList = this.handleSortFormatter(this.invoicesItems);
      // search for the index and the item in the List.
      const indexFromFormattedList = formattedInvoicesList.findIndex((_invoice) => +_invoice.id === +invoice.id);
      const itemFromFormattedList = formattedInvoicesList.find((_invoice) => +_invoice.id === +invoice.id);
      // check if validation status changed in case of electronic invoice and the user cannot approve it anymore if it is out of his validation level.
      const isUserCanValidate = !!(itemFromFormattedList && indexFromFormattedList !== -1 && itemFromFormattedList.validated >= invoice.validated);
      // reload the list of the electronic invoices to receive.
      if (this.forApprove) this.$emit('list:electronic', latestSelectedElectronicInvoiceId);
      // now handle the review mode in PC screen, else just open the modal.
      if (this.forApprove && this.$screen.width > 992) {
        // if electronic invoice and the user cannot validate it anymore, make sur to keep the data so the user will be able to continue to update it without validating.
        if (invoice.subType === INVOICE_SUBTYPE.ELEC_INVOICE) {
          const fullInvoiceData = await getSpecificExpense(+invoice.id);
          const newData = { ...fullInvoiceData, statusClass: null, validatedStatus: null, userCanValidate: isUserCanValidate };
          if (isUserCanValidate || indexFromFormattedList !== -1) {
            formattedInvoicesList.splice(indexFromFormattedList, 1, newData); // replace old data by the new one.
          } else {
            formattedInvoicesList.push(newData); // push new data at the end of the list.
          }
        } else {
          formattedInvoicesList[indexFromFormattedList] = { ...invoice, userCanValidate: isUserCanValidate };
        }
        const realIndex = index ? index : indexFromFormattedList !== -1 || isUserCanValidate ? indexFromFormattedList : formattedInvoicesList.length - 1;
        this.$emit('open:details', formattedInvoicesList, realIndex);
        this.isLoading = false;
      } else {
        try {
          const isForEfc = !this.forMultipleInvoice && (this.forEfcDraft || this.forEfcSubmitted);
          // if electronic invoice and the user cannot validate it anymore, make sur to keep the data so the user will be able to continue to update it without validating.
          let realInvoiceData = {};
          if (invoice.subType === INVOICE_SUBTYPE.ELEC_INVOICE) {
            const fullInvoiceData = await getSpecificExpense(+invoice.id);
            realInvoiceData = { ...fullInvoiceData, statusClass: null, validatedStatus: null, userCanValidate: isUserCanValidate };
          } else {
            realInvoiceData = { ...invoice, userCanValidate: isUserCanValidate };
          }
          const invoicePayload = await this.preprareDetailsPayload(realInvoiceData, isForEfc);
          this.$emit('open:details', invoicePayload);
          this.isLoading = false;
        } catch (error) {
          console.log('Error on getting Details for invoice: ', error);
          this.isLoading = false;
        }
      }
    },
    updateSelectedRows(selectedItem) {
      const foundIndex = this.selectedRows.findIndex((x) => x.id === selectedItem.id);
      if (foundIndex !== -1) {
        this.selectedRows.splice(foundIndex, 1);
      } else this.selectedRows.push(selectedItem);

      this.isSelectedAll = this.selectedRows.length === this.invoicesItems.length;

      this.emitSelection();
    },
    setSelectionForAll() {
      if (this.selectedRows.length !== this.invoicesItems.length) {
        this.selectedRows = _.cloneDeep(this.invoicesItems);
        this.invoicesItems = this.invoicesItems.map((x) => {
          return { ...x, selectState: true };
        });
      } else {
        this.selectedRows = [];
        this.invoicesItems = this.invoicesItems.map((x) => {
          return { ...x, selectState: false };
        });
      }
      this.isSelectedAll = !this.isSelectedAll;

      this.emitSelection();
    },
    emitSelection() {
      let element = '';
      this.forApprove ? (element = 'approve') : (element = 'draft');
      this.$emit('selection:change', this.selectedRows, element);
    },
    setSelectedItem(event) {
      this.selectedRows = event;
    }
  }
};
</script>

<style lang="scss" scoped>
:deep(.vertical-centered) {
  vertical-align: middle !important;
}

:deep(.tr-cursor) {
  cursor: pointer !important;
}

.warning-invoice {
  color: #bc9581;
  border-color: #bc9581;
}

.text-description-overflow-ellypsis {
  max-height: 40px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2; /* Ensure line clamp works */
}

#select-all .custom-control-input:checked ~ .custom-control-label::before {
  color: #fff;
  border: 2px solid #fff;
  background-color: #00a09c;
}

#filterInput {
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
}
.input-group-text {
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
}
</style>
